import { useMutation, useQueryClient } from '@tanstack/react-query';

import { DEALS_QUEUE_QUERY_KEY } from '../../deals/api';
import {
  cancelAllBidsForCar,
  relayAction,
  relayClaimAction,
  setBidProcessed,
  setMinimumBid,
  syncBidStatuses,
  updateBidStatus,
} from '../api';
import { type BidStatus, type RelayAction, type RelayClaimAction } from '../types';

/** Query keys for the admin bids surfaces. */
export const BIDS_QUERY_KEY = ['bids'] as const;
export const RELAY_QUEUE_QUERY_KEY = ['relay-queue'] as const;

/**
 * Invalidate every bids-derived query after a mutation succeeds — including
 * the «Угоди» queue, whose rows are a read model over these same bids.
 */
function useInvalidateBids() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: BIDS_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: RELAY_QUEUE_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: DEALS_QUEUE_QUERY_KEY });
  };
}

/** Query key for the per-lot minimum-bid floor. */
export const MINIMUM_BID_QUERY_KEY = ['minimum-bid'] as const;

/**
 * PATCH a bid's auction status, then refresh the list + detail.
 *
 * `forceReplace` completes the winner-replacement handshake — the caller
 * re-issues with true after the operator confirms the 409.
 */
export function useUpdateBidStatus() {
  const invalidate = useInvalidateBids();
  return useMutation({
    mutationFn: ({
      id,
      status,
      forceReplace,
    }: {
      id: string;
      status: BidStatus;
      forceReplace?: boolean;
    }) => updateBidStatus(id, status, forceReplace ?? false),
    onSuccess: invalidate,
  });
}

/** Set the lot's minimum-bid floor; refreshes bids and the floor query. */
export function useSetMinimumBid() {
  const invalidate = useInvalidateBids();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ carId, minimumBidPrice }: { carId: string; minimumBidPrice: number }) =>
      setMinimumBid(carId, minimumBidPrice),
    onSuccess: () => {
      invalidate();
      void queryClient.invalidateQueries({ queryKey: MINIMUM_BID_QUERY_KEY });
    },
  });
}

/** Void every still-live bid on a lot, then refresh. */
export function useCancelAllBids() {
  const invalidate = useInvalidateBids();
  return useMutation({
    mutationFn: ({ carId }: { carId: string }) => cancelAllBidsForCar(carId),
    onSuccess: invalidate,
  });
}

/** Toggle a bid's processed-on-auction flag, then refresh. */
export function useSetBidProcessed() {
  const invalidate = useInvalidateBids();
  return useMutation({
    mutationFn: ({ id, processed }: { id: string; processed: boolean }) =>
      setBidProcessed(id, processed),
    onSuccess: invalidate,
  });
}

/** Trigger the system-wide bid-status reconciliation. */
export function useSyncBidStatuses() {
  const invalidate = useInvalidateBids();
  return useMutation({
    mutationFn: () => syncBidStatuses(),
    onSuccess: invalidate,
  });
}

/**
 * Phase 13 — take / hand back the operator claim on a relay row. Refreshes the
 * queue so the «у роботі» badge every colleague sees follows the click.
 */
export function useRelayClaim() {
  const invalidate = useInvalidateBids();
  return useMutation({
    mutationFn: ({ id, action, reason }: { id: string; action: RelayClaimAction; reason?: string }) =>
      relayClaimAction(id, action, reason ? { reason } : undefined),
    onSuccess: invalidate,
  });
}

/** Perform a relay-desk action (queue / confirm-relay / outcome / cancel / reject). */
export function useRelayAction() {
  const invalidate = useInvalidateBids();
  return useMutation({
    mutationFn: ({
      id,
      action,
      body,
    }: {
      id: string;
      action: RelayAction;
      body?: Record<string, unknown>;
    }) => relayAction(id, action, body),
    onSuccess: invalidate,
  });
}
